import '@/playlist/games/timeGame/styles/time-game-end-screen.scss';

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { gsap, Power1 } from 'gsap';
import Lottie from 'lottie-react';

import useResponsive from '@/services/hocs/useResponsive';
import { EndOutcome } from '@/playlist/types';

import Button from '@/components/button/Button';
import GameEndScreenMilepost from '@/playlist/games/common/components/GameEndScreenMilepost';
import TimeGameHeader from '@/playlist/games/timeGame/components/common/TimeGameHeader';

import BackgroundFailedMobile from '@/playlist/games/timeGame/assets/images/end-screen-failed-mobile.svg?react';
import BackgroundFailed from '@/playlist/games/timeGame/assets/images/end-screen-failed.svg?react';
import BackgroundSuccessMobile from '@/playlist/games/timeGame/assets/images/end-screen-success-mobile.svg?react';
import BackgroundSuccess from '@/playlist/games/timeGame/assets/images/end-screen-success.svg?react';
import qSpaceFailed from '@/playlist/games/timeGame/assets/lottie/q-space-failed.json';
import qSpaceSuccess from '@/playlist/games/timeGame/assets/lottie/q-space-station.json';

export type EndScreenProps = {
	className?: string;
	outcome: EndOutcome;
	progressStarsEarned?: number;
	totalStars?: number;
	earnedStars?: number;
	previousEarned?: number;
	levelImage?: string;
	levelName?: string;
	levelTitle?: string;
	levelCompleted?: boolean;
	totalQuestions?: number;
	wrongAnswers?: number;
	onReplay?: () => void;
	onClose: () => void;
};

const EndScreen = ({
	className,
	outcome,
	progressStarsEarned,
	totalStars,
	earnedStars,
	previousEarned,
	levelImage,
	levelName,
	levelTitle,
	levelCompleted,
	totalQuestions,
	wrongAnswers,
	onReplay,
	onClose,
}: EndScreenProps) => {
	const { t } = useTranslation();
	const { isPortrait } = useResponsive();

	const [showProgressAnimation, setShowProgressAnimation] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const milepostContainerRef = useRef<HTMLDivElement>(null);

	const isSuccess = outcome === EndOutcome.SUCCEEDED;
	const isFailed = outcome === EndOutcome.LOST;

	const Background = isPortrait
		? isSuccess
			? BackgroundSuccessMobile
			: BackgroundFailedMobile
		: isSuccess
		? BackgroundSuccess
		: BackgroundFailed;
	const characterAnimation = isSuccess ? qSpaceSuccess : qSpaceFailed;

	const handleClickNext = () => {
		if (totalStars !== undefined && earnedStars !== undefined) {
			const tl = gsap.timeline();

			tl.to(contentRef.current, {
				autoAlpha: 0,
				y: 50,
				zIndex: -1,
				duration: 0.4,
				ease: Power1.easeInOut,
			});

			tl.to(milepostContainerRef.current, {
				autoAlpha: 1,
				duration: 0.5,
				ease: Power1.easeInOut,
				onComplete: () => {
					setShowProgressAnimation(true);
				},
			});
		} else {
			onClose();
		}
	};

	const handleReplay = () => {
		if (onReplay) {
			onReplay();
		} else {
			onClose();
		}
	};

	return (
		<>
			<TimeGameHeader onBack={onClose} />
			<div
				className={classNames(
					'time-game-end-screen',
					{
						'time-game-end-screen--succeeded': isSuccess,
						'time-game-end-screen--failed': isFailed,
					},
					className,
				)}
			>
				<div className="time-game-end-screen__background">
					<Background />
				</div>

				<Lottie
					animationData={characterAnimation}
					loop
					autoplay
					className="time-game-end-screen__character"
					aria-hidden="true"
				/>

				{totalStars !== undefined && earnedStars !== undefined && (
					<GameEndScreenMilepost
						ref={milepostContainerRef}
						progressStarsEarned={progressStarsEarned}
						totalStars={totalStars}
						earnedStars={earnedStars}
						previousEarned={previousEarned}
						levelImage={levelImage}
						levelName={levelName}
						levelTitle={levelTitle}
						levelCompleted={levelCompleted}
						showProgressAnimation={showProgressAnimation}
						onReplay={handleReplay}
						onClose={onClose}
						replayButtonLabel={t('time_game.endscreen.replay')}
						closeButtonLabel={t('time_game.endscreen.close')}
					/>
				)}

				<div className="time-game-end-screen__inner" ref={contentRef}>
					<h2 className="time-game-end-screen__title">
						{isSuccess
							? t('time_game.endscreen.title_success')
							: t('time_game.endscreen.title_failed')}
					</h2>
					<p className="time-game-end-screen__description">
						{isSuccess
							? t('time_game.endscreen.description_success')
							: t('time_game.endscreen.description_failed', {
									wrongAnswers: wrongAnswers ?? 0,
									totalQuestions: totalQuestions ?? 0,
							  })}
					</p>
					<div className="time-game-end-screen__actions">
						<Button
							type="primary"
							iconName="icon-arrow-circle-right"
							title={t('time_game.endscreen.next_button')}
							onClick={handleClickNext}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default EndScreen;
